import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebase';
import {
  Typography, Paper, Box, Button, MenuItem, TextField, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';

const listAdminUsers = httpsCallable(functions, 'listAdminUsers');
const setUserRole = httpsCallable(functions, 'setUserRole');

const ROLE_OPTIONS = ['VIEWER', 'VIEWER_LOCALITY', 'EDITOR', 'ADMIN', 'OWNER'];

// Everyone who currently holds a dashboard role, with a control to
// upgrade/downgrade or revoke each person's access. Different from Access
// Requests, which only covers new/pending requests - this is the
// existing-user roster. VIEWER_LOCALITY needs one extra piece of
// information (which locality it's scoped to) before it can be applied, so
// selecting it doesn't save immediately like the other roles do - it opens
// an inline locality field with its own Save button.
export default function ManageAccessPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [pendingLocalityRole, setPendingLocalityRole] = useState({}); // uid -> localityInput

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setMessage(null);
    try {
      const res = await listAdminUsers();
      setUsers(res.data?.items || []);
    } catch (error) {
      console.error('Failed to load admin users:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }

  async function applyRole(user, role, locality) {
    setBusyUid(user.uid);
    setMessage(null);
    try {
      await setUserRole({ uid: user.uid, role, locality });
      setUsers((prev) => prev.map((u) => (u.uid === user.uid ? { ...u, roles: [role], locality: role === 'VIEWER_LOCALITY' ? locality : null } : u)));
      setMessage({ type: 'success', text: role === 'VIEWER_LOCALITY' ? `${user.email} is now VIEWER_LOCALITY for ${locality}.` : `${user.email} is now ${role}.` });
      setPendingLocalityRole((prev) => { const next = { ...prev }; delete next[user.uid]; return next; });
    } catch (error) {
      console.error('Failed to change role:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyUid(null);
    }
  }

  function handleRoleSelect(user, role) {
    if (role === 'VIEWER_LOCALITY') {
      setPendingLocalityRole((prev) => ({ ...prev, [user.uid]: user.locality || '' }));
    } else {
      setPendingLocalityRole((prev) => { const next = { ...prev }; delete next[user.uid]; return next; });
      applyRole(user, role, undefined);
    }
  }

  async function confirmRevoke() {
    if (!revokeTarget) return;
    setBusyUid(revokeTarget.uid);
    setMessage(null);
    try {
      await setUserRole({ uid: revokeTarget.uid, role: '' });
      setUsers((prev) => prev.filter((u) => u.uid !== revokeTarget.uid));
      setMessage({ type: 'success', text: `Revoked access for ${revokeTarget.email}.` });
      setRevokeTarget(null);
    } catch (error) {
      console.error('Failed to revoke access:', error);
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyUid(null);
    }
  }

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ maxWidth: 800 }}>
      {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
      {users.length === 0 ? (
        <Typography color="text.secondary">No one has dashboard access yet.</Typography>
      ) : users.map((user) => {
        const isSelf = user.uid === auth.currentUser?.uid;
        const currentRole = user.roles[0] || 'VIEWER';
        const editingLocality = Object.prototype.hasOwnProperty.call(pendingLocalityRole, user.uid);
        const selectValue = editingLocality ? 'VIEWER_LOCALITY' : currentRole;
        return (
          <Paper key={user.uid} variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography>
                {user.displayName ? `${user.displayName} (${user.email})` : user.email}{isSelf ? ' (you)' : ''}
              </Typography>
              {currentRole === 'VIEWER_LOCALITY' && user.locality && (
                <Typography variant="caption" color="text.secondary" display="block">Locality: {user.locality}</Typography>
              )}
              {user.lastSignInTime && (
                <Typography variant="caption" color="text.secondary">
                  Last signed in: {new Date(user.lastSignInTime).toLocaleString()}
                </Typography>
              )}
            </Box>
            <TextField
              select size="small" label="Role" sx={{ width: 160 }}
              value={selectValue}
              disabled={isSelf || busyUid === user.uid}
              onChange={(e) => handleRoleSelect(user, e.target.value)}
            >
              {ROLE_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            {editingLocality && (
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small" label="Locality" sx={{ width: 160 }}
                  value={pendingLocalityRole[user.uid]}
                  onChange={(e) => setPendingLocalityRole((prev) => ({ ...prev, [user.uid]: e.target.value }))}
                  disabled={busyUid === user.uid}
                />
                <Button
                  size="small" variant="contained" disabled={busyUid === user.uid || !pendingLocalityRole[user.uid]?.trim()}
                  onClick={() => applyRole(user, 'VIEWER_LOCALITY', pendingLocalityRole[user.uid].trim())}
                >
                  Save
                </Button>
              </Stack>
            )}
            <Button
              color="error" size="small" disabled={isSelf || busyUid === user.uid}
              onClick={() => setRevokeTarget(user)}
            >
              Revoke access
            </Button>
          </Paper>
        );
      })}

      <Dialog open={!!revokeTarget} onClose={() => (busyUid ? null : setRevokeTarget(null))}>
        <DialogTitle>Revoke access for {revokeTarget?.email}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            They will immediately lose access to the admin dashboard. You can re-approve them later if needed, but
            they'd need to request access again first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={!!busyUid}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRevoke} disabled={!!busyUid}>
            {busyUid ? 'Revoking...' : 'Confirm Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
